/**
 * "What's happening" (G8): a spoken cross-project summary built only from verified state — the
 * unified run state (runs working / waiting / failed), decrypted inbox asks and the G13 digests
 * (landed, released, failures; evidence-backed, never agent claims). Pure, so the watch and the
 * phone can speak the same words. Numbers are exact; nothing is rounded or truncated.
 */
import {
  CHANGELOG_KINDS,
  type ChangelogKind,
  type DigestReport,
  type DigestSection,
} from '@viko/protocol'

export interface SummaryRun {
  runId: string
  projectId?: string
  status: string
  title?: string
  blockedOn?: string
}
export interface SummaryAsk {
  id: string
  runId: string
  projectId?: string
  kind: 'question' | 'approval'
  title: string
  body: string
  blocking: boolean
  createdAt: number
  /** Question option labels (first question), for "answer the first question: …". */
  options?: string[]
}
export interface SummaryInput {
  now: number
  projects: readonly { id: string; name: string; goal?: string }[]
  runs: readonly SummaryRun[]
  asks: readonly SummaryAsk[]
  digests: readonly DigestReport[]
  /**
   * The app-wide needs-you count (lib/needs-you.ts). When given, Voice speaks it ("2 need you")
   * instead of counting waiting runs and asks itself, so it matches every badge.
   */
  needsYou?: { total: number; of: (projectId: string) => number }
}

export interface ProjectBrief {
  id: string
  name: string
  goal?: string
  working: number
  waiting: number
  /** Needs-you count for the project (the shared selector when given, else waiting runs). */
  needs: number
  failed: number
  asks: number
  landed: number
  /** One sentence for the overview. */
  short: string
  runs: { working: SummaryRun[]; waiting: SummaryRun[]; failed: SummaryRun[] }
  section?: DigestSection
}
export interface SpokenAsk extends SummaryAsk {
  projectName: string
}
export interface VoiceSummary {
  spoken: string
  projects: ProjectBrief[]
  quiet: string[]
  /** Numbered in speaking order ("the first question" = asks[0]). */
  asks: SpokenAsk[]
}

const DAY = 86_400_000
const WORKING = new Set(['running', 'compacting', 'starting', 'queued'])
const WAITING = new Set(['waiting'])
const FAILED = new Set(['error', 'failed'])

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`
const needYou = (n: number) => `${n} ${n === 1 ? 'needs' : 'need'} you`
const KIND_PHRASE: Record<ChangelogKind, (n: number) => string> = {
  release: (n) => `${n} released`,
  landed: (n) => `${n} landed`,
  build: (n) => plural(n, 'build'),
  decision: (n) => plural(n, 'decision'),
  'task-done': (n) => `${plural(n, 'task')} done`,
  'task-blocked': (n) => `${n} blocked`,
  failure: (n) => plural(n, 'failure'),
}
const ORDINAL = [
  'First',
  'Second',
  'Third',
  'Fourth',
  'Fifth',
  'Sixth',
  'Seventh',
  'Eighth',
  'Ninth',
  'Tenth',
]
export const ordinal = (i: number) => ORDINAL[i] ?? `Number ${i + 1}`

/** "A", "A, or B", "A, B, or C". */
function orList(xs: readonly string[]) {
  if (xs.length <= 1) return xs.join('')
  return `${xs.slice(0, -1).join(', ')}, or ${xs.at(-1)}`
}
const sentence = (s: string) => (/[.!?]$/.test(s.trim()) ? s.trim() : `${s.trim()}.`)

/** Newest digest section per project from digests that ended within the last day. */
function recentSections(digests: readonly DigestReport[], now: number) {
  const out = new Map<string, { to: number; section: DigestSection }>()
  for (const d of digests) {
    if (d.to < now - DAY) continue
    for (const section of d.sections) {
      const had = out.get(section.projectId)
      if (!had || d.to > had.to) out.set(section.projectId, { to: d.to, section })
    }
  }
  return new Map([...out].map(([k, v]) => [k, v.section]))
}

export function buildVoiceSummary(i: SummaryInput): VoiceSummary {
  if (!i.projects.length) return { spoken: 'No projects yet.', projects: [], quiet: [], asks: [] }
  const names = new Map(i.projects.map((p) => [p.id, p.name]))
  const sections = recentSections(i.digests, i.now)
  const briefs: ProjectBrief[] = []
  const quiet: string[] = []
  for (const p of i.projects) {
    const runs = i.runs.filter((r) => r.projectId === p.id)
    const working = runs.filter((r) => WORKING.has(r.status))
    const waiting = runs.filter((r) => WAITING.has(r.status))
    const failed = runs.filter((r) => FAILED.has(r.status))
    const asks = i.asks.filter((a) => a.projectId === p.id)
    const section = sections.get(p.id)
    const counts = section?.counts ?? {}
    const digestParts = CHANGELOG_KINDS.filter((k) => (counts[k] ?? 0) > 0).map((k) =>
      KIND_PHRASE[k](counts[k]!),
    )
    const needs = i.needsYou ? i.needsYou.of(p.id) : undefined
    const parts = [
      ...(working.length ? [`${working.length} working`] : []),
      ...(needs !== undefined
        ? needs
          ? [needYou(needs)]
          : []
        : waiting.length
          ? [`${waiting.length} waiting on you`]
          : []),
      ...(failed.length ? [`${failed.length} failed`] : []),
      // Asks not already counted as a waiting run.
      ...(needs === undefined && asks.length > waiting.length
        ? (['approval', 'question'] as const)
            .map((k) => [k, asks.filter((a) => a.kind === k).length] as const)
            .filter(([, n]) => n > 0)
            .map(([k, n]) => plural(n, k))
        : []),
      ...digestParts,
    ]
    if (!parts.length) {
      quiet.push(p.name)
      continue
    }
    briefs.push({
      id: p.id,
      name: p.name,
      ...(p.goal ? { goal: p.goal } : {}),
      working: working.length,
      waiting: waiting.length,
      needs: needs ?? waiting.length,
      failed: failed.length,
      asks: asks.length,
      landed: counts.landed ?? 0,
      short: `${p.name}: ${parts.join(', ')}.`,
      runs: { working, waiting, failed },
      ...(section ? { section } : {}),
    })
  }
  const score = (b: ProjectBrief) =>
    b.asks * 100 + b.waiting * 50 + b.failed * 30 + b.working * 10 + b.landed
  briefs.sort((a, b) => score(b) - score(a) || a.name.localeCompare(b.name))

  const asks: SpokenAsk[] = i.asks
    .filter((a) => !a.projectId || names.has(a.projectId))
    .map((a) => ({ ...a, projectName: (a.projectId && names.get(a.projectId)) || 'a project' }))
    .sort(
      (a, b) =>
        Number(b.blocking) - Number(a.blocking) ||
        a.createdAt - b.createdAt ||
        a.id.localeCompare(b.id),
    )

  const total = (k: 'working' | 'waiting' | 'failed') => briefs.reduce((n, b) => n + b[k], 0)
  const head = [
    ...(total('working') ? [`${plural(total('working'), 'run')} working`] : []),
    ...(i.needsYou
      ? i.needsYou.total
        ? [needYou(i.needsYou.total)]
        : []
      : total('waiting')
        ? [`${total('waiting')} waiting on you`]
        : []),
    ...(total('failed') ? [`${total('failed')} failed`] : []),
  ]
  const lines = [plural(i.projects.length, 'project') + '.']
  if (head.length) lines.push(`${head.join(', ')}.`)
  if (asks.length && !i.needsYou) lines.push(`${plural(asks.length, 'question')} for you.`)
  if (!briefs.length) {
    lines.push('Nothing running and nothing needs you.')
    return { spoken: lines.join(' '), projects: briefs, quiet, asks }
  }
  if (!head.length && !asks.length) lines.push('Nothing running and nothing needs you.')
  for (const b of briefs) lines.push(b.short)
  if (quiet.length) lines.push(`Quiet: ${orList(quiet).replace(', or ', ', and ')}.`)
  return { spoken: lines.join(' '), projects: briefs, quiet, asks }
}

/** "Tell me more about Viko". */
export function projectDetail(s: VoiceSummary, projectId: string): string {
  const b = s.projects.find((p) => p.id === projectId)
  if (!b) return 'I have nothing on that project.'
  const out = [`${b.name}.`]
  if (b.goal) out.push(`Goal: ${sentence(b.goal)}`)
  const title = (r: SummaryRun) => r.title?.trim() || 'an untitled run'
  if (b.runs.working.length) out.push(`Working: ${b.runs.working.map(title).join('; ')}.`)
  if (b.runs.waiting.length)
    out.push(
      `Waiting on you: ${b.runs.waiting
        .map((r) => (r.blockedOn ? `${title(r)}, ${r.blockedOn}` : title(r)))
        .join('; ')}.`,
    )
  if (b.runs.failed.length) out.push(`Failed: ${b.runs.failed.map(title).join('; ')}.`)
  const mine = s.asks.filter((a) => a.projectId === projectId)
  if (mine.length)
    out.push(
      `${plural(mine.length, 'question')} for you: ${mine
        .map((a) => `${ordinal(s.asks.indexOf(a)).toLowerCase()} in your list`)
        .join(', ')}.`,
    )
  const items = b.section?.items ?? []
  for (const kind of CHANGELOG_KINDS) {
    const of = items.filter((x) => x.kind === kind)
    if (!of.length) continue
    const n = b.section?.counts[kind] ?? of.length
    const shown = of.slice(0, 5).map((x) => x.title)
    const more = n - shown.length
    out.push(`${DETAIL_LABEL[kind]}: ${shown.join('; ')}${more > 0 ? `; and ${more} more` : ''}.`)
  }
  return out.join(' ')
}
const DETAIL_LABEL: Record<ChangelogKind, string> = {
  release: 'Released',
  landed: 'Landed',
  build: 'Builds',
  decision: 'Decided',
  'task-done': 'Done',
  'task-blocked': 'Blocked',
  failure: 'Failures',
}

/** "Read the questions": numbered, with the first question's options. */
export function spokenAsks(s: VoiceSummary): string {
  if (!s.asks.length) return 'Nothing needs you right now.'
  return s.asks
    .map((a, i) => {
      const opts = a.options?.length ? ` Options: ${sentence(orList(a.options))}` : ''
      return `${ordinal(i)}, ${a.projectName} ${a.kind}: ${sentence(a.body || a.title)}${opts}`
    })
    .join(' ')
}

/** The ask a spoken ordinal refers to (-1 = last). */
export function askAt(s: VoiceSummary, index: number): SpokenAsk | undefined {
  return index === -1 ? s.asks.at(-1) : s.asks[index]
}
